/**
 * AgentConsoleWidget — Sep 2026 · r-agent-feed
 *
 * A small bottom-right strip that mirrors real workspace agent activity
 * so people feel the system working post-login. Consumes the same
 * `useAgentsLive` feed as the sidebar chip so lines are true.
 *
 * Behaviour:
 *   · Cycles through LIVE agents only (idle ones are skipped) every
 *     ~5-9s.
 *   · Randomly hidden most of the time to avoid distraction: a visible
 *     "session" lasts 12-25s, then hides for 60-180s, then reappears.
 *   · Session-scoped dismiss button — full reload restores.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { X } from "lucide-react";
import { useAgentsLive } from "@/lib/useAgentsLive";

export default function AgentConsoleWidget() {
  const feed = useAgentsLive();
  const liveAgents = useMemo(
    () => (feed?.agents || []).filter((a) => a.state === "live"),
    [feed?.agents],
  );

  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem("acq-agent-console-dismissed") === "1"; }
    catch { return false; }
  });
  const [visible, setVisible] = useState(false);
  const [idx, setIdx] = useState(0);
  const timers = useRef([]);

  // Show/hide session loop: appears for 12-25s, hides for 60-180s.
  useEffect(() => {
    if (dismissed) return undefined;
    let alive = true;
    const clear = () => { timers.current.forEach(clearTimeout); timers.current = []; };
    const schedule = (fn, ms) => {
      const t = setTimeout(fn, ms);
      timers.current.push(t);
      return t;
    };
    const show = () => {
      if (!alive) return;
      setVisible(true);
      const dur = 12000 + Math.floor(Math.random() * 13000);
      schedule(hide, dur);
    };
    const hide = () => {
      if (!alive) return;
      setVisible(false);
      const gap = 60000 + Math.floor(Math.random() * 120000);
      schedule(show, gap);
    };
    // First appearance ~8-20s after mount so it doesn't fight the initial page load.
    schedule(show, 8000 + Math.floor(Math.random() * 12000));
    return () => { alive = false; clear(); };
  }, [dismissed]);

  // Cycle the shown agent while visible.
  useEffect(() => {
    if (!visible || liveAgents.length <= 1) return undefined;
    let alive = true;
    const step = () => {
      if (!alive) return;
      setIdx((i) => (i + 1) % Math.max(liveAgents.length, 1));
      const t = setTimeout(step, 5000 + Math.floor(Math.random() * 4000));
      timers.current.push(t);
    };
    const first = setTimeout(step, 5000 + Math.floor(Math.random() * 4000));
    timers.current.push(first);
    return () => { alive = false; };
  }, [visible, liveAgents.length]);

  const dismiss = () => {
    setDismissed(true);
    try { sessionStorage.setItem("acq-agent-console-dismissed", "1"); } catch { /* ignore */ }
  };

  if (dismissed || !visible || liveAgents.length === 0) return null;

  const agent = liveAgents[idx % liveAgents.length];

  return (
    <div
      className="fixed bottom-4 right-4 z-40 pointer-events-auto"
      role="status"
      aria-live="polite"
      data-testid="agent-console-widget"
    >
      <div
        className="flex items-center gap-2 border border-border bg-background/95 backdrop-blur-md shadow-lg px-3 py-1.5 text-[11px] max-w-sm login-fade-in"
        key={`${agent.key}-${idx}`}
      >
        <span className="relative flex items-center justify-center shrink-0" aria-hidden="true">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
          <span className="absolute w-3 h-3 rounded-full bg-emerald-500/30 animate-ping" />
        </span>
        <span className="font-semibold whitespace-nowrap">{agent.label}</span>
        <span className="text-muted-foreground">·</span>
        <span className="truncate">{agent.activity}</span>
        <button
          type="button"
          onClick={dismiss}
          className="ml-1 shrink-0 text-muted-foreground hover:text-foreground p-0.5"
          aria-label="Dismiss agent console"
          data-testid="agent-console-dismiss"
        >
          <X className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}
